/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type EmailResponsesSettings from 'embercom/models/ai-agent/email-responses-settings';
import type FinUsageService from 'embercom/services/fin-usage-service';

const SECTION_MODEL_MAP: any = {
  'suggestion-settings': 'aiAgentSettings',
  'answer-bot': '',
};

// https://app.intercom.com/a/apps/tx2p130c/outbound/survey/35658277

interface SettingsArgs {
  initialSection?: string;
  aiAgentQuickReplySettings: any;
  aiAgentSettings: any;
  aiAgentToneOfVoiceSettings: any;
  aiAgentMultilingualSettings: any;
  aiAgentEmailResponsesSettings: EmailResponsesSettings;
  previewConfiguration: any;
  languages: any;
}

export default class Settings extends Component<SettingsArgs> {
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare notificationsService: any;
  @service declare permissionsService: any;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare customerService: $TSFixMe;
  @service declare finUsageService: FinUsageService;

  @tracked personalityCustomizationSettings?: Settings;
  @tracked isUsageDrawerOpen = false;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.getPersonalityCustomizationSettings();

    if (this.args.initialSection) {
      this.scrollToSection(this.args.initialSection);
    }
  }

  async getPersonalityCustomizationSettings(): Promise<$TSFixMe> {
    this.personalityCustomizationSettings = await this.store.findRecord(
      'ai-agent/tone-of-voice-settings',
      this.appService.app.id,
    );
  }

  get guidelines(): Guideline[] {
    return this.store.peekAll('ai-agent/guidelines').toArray();
  }

  get app() {
    return this.appService.app;
  }

  get hasNoSetLimit() {
    return !this.finUsageService.isFinResolutionsLimitValid;
  }

  get hasLimitSetNotReached() {
    return (
      this.finUsageService.isFinResolutionsLimitValid &&
      !this.finUsageService.isFinResolutionsLimitReached
    );
  }

  get hasLimitReached() {
    return this.finUsageService.isFinResolutionsLimitReached;
  }

  get usageLimitState() {
    if (this.hasLimitReached) {
      return 'limit_reached';
    }
    if (this.hasLimitSetNotReached) {
      return 'limit_set_not_reached';
    }
    return 'no_set_limit';
  }

  get isInFreeTrial() {
    return this.finUsageService.isInTrial;
  }

  get shouldShowAlertsAndLimitsCardCta() {
    return !this.isInFreeTrial;
  }

  @action sectionDidOpen(sectionName: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      context: 'operator_settings',
      object: `${sectionName}-section`,
    });
  }

  @action sectionDidClose(sectionName: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'closed',
      context: 'operator_settings',
      object: `${sectionName}-section`,
    });
  }

  @action async sectionCanClose(sectionName: string) {
    let model = SECTION_MODEL_MAP[sectionName];
    let confirmNotSaved = {
      title: this.intl.t('operator.settings.confirm-not-saved.close-without-saving'),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t('operator.settings.confirm-not-saved.continue-without-saving'),
      cancelButtonText: this.intl.t('operator.settings.confirm-not-saved.keep-editing'),
      body: this.intl.t('operator.settings.confirm-not-saved.body'),
    };

    if (!model || !model.hasDirtyAttributes) {
      return true;
    }

    let confirmed = await this.intercomConfirmService.confirm(confirmNotSaved);

    if (confirmed) {
      model.rollbackAttributes();
      return true;
    } else {
      return false;
    }
  }

  @action openUsageDrawer() {
    this.isUsageDrawerOpen = true;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'set_alerts_and_limits_cta',
      context: 'operator_settings',
      section: 'alerts_and_limits',
      state: this.usageLimitState,
    });
  }

  @action
  onUsageDrawerOpenChange(isOpen: boolean) {
    this.isUsageDrawerOpen = isOpen;
  }

  scrollToSection(sectionId: string) {
    requestAnimationFrame(() => {
      let element = document.querySelector(`[data-accordion-section="${sectionId}"]`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Settings': typeof Settings;
  }
}
